'use client';

import { AlertCircle, ArrowLeft, ExternalLink, Maximize2, Minimize2, X } from 'lucide-react';
import Image from 'next/image';
import type { SyntheticEvent } from 'react';
import { useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
export interface LinkIframeViewerProperties {
  currentIframeTitle: string;
  domain: string;
  faviconUrl?: string | null;
  iframeError: boolean;
  iframeUrl: string | null;
  isFullscreen: boolean;
  isIframeLoading: boolean;
  metadataTitle?: string | null;
  onBack: () => void;
  onClose: () => void;
  onIframeError: () => void;
  onIframeLoad: (title?: string) => void;
  onOpenInNewTab: () => void;
  onToggleFullscreen: () => void;
  url: string;
}
export function LinkIframeViewer(properties: LinkIframeViewerProperties) {
  const {
    url,
    iframeUrl,
    isIframeLoading,
    iframeError,
    isFullscreen,
    currentIframeTitle,
    faviconUrl,
    domain,
    onBack,
    onClose,
    onToggleFullscreen,
    onOpenInNewTab,
    onIframeLoad,
    onIframeError,
    metadataTitle
  } = properties;
  const iframeReference = useRef<HTMLIFrameElement | null>(null);
  const handleIframeLoad = useCallback((event: SyntheticEvent<HTMLIFrameElement>) => {
    const fallbackTitle = metadataTitle ?? '';
    try {
      const iframe = event.currentTarget;
      const iframeDocument = iframe.contentDocument ?? iframe.contentWindow?.document ?? null;
      onIframeLoad(iframeDocument?.title ?? fallbackTitle);
    } catch {
      onIframeLoad(fallbackTitle);
    }
  }, [metadataTitle, onIframeLoad]);
  return <div className={cn('mt-3 border border-border/60 rounded-2xl overflow-hidden bg-card/95 backdrop-blur-xl shadow-lg', isFullscreen ? 'mt-0 h-full rounded-none border-0' : '')}>
      <TooltipProvider>
        <div className="flex items-center gap-2 border-b border-border/60 bg-muted/20 px-3 py-2">
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onBack}>
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Back</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
                  <X className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Close preview</TooltipContent>
            </Tooltip>
          </div>

          <div className="flex-1 flex items-center gap-2 min-w-0">
            {faviconUrl ? <Image key={faviconUrl} src={faviconUrl} alt={domain} width={16} height={16} className="h-4 w-4 flex-shrink-0 rounded-sm" unoptimized onError={event_0 => {
            event_0.currentTarget.style.display = 'none';
          }} /> : null}
            <div className="flex flex-col min-w-0">
              {currentIframeTitle ? <span className="text-xs font-medium text-foreground truncate">{currentIframeTitle}</span> : null}
              <span className="text-xs text-muted-foreground truncate">{iframeUrl || url}</span>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onToggleFullscreen}>
                  {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onOpenInNewTab}>
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Open in new tab</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </TooltipProvider>

      <div className={cn('relative', isFullscreen ? 'h-[calc(100%-48px)]' : 'h-[600px]')}>
        {isIframeLoading && <div className="absolute inset-0 flex items-center justify-center bg-card/90 z-layer-surface">
            <div className="flex flex-col items-center gap-3">
              <div className="w-6 h-6 animate-spin rounded-full border-2 border-border/50 border-t-transparent" />
              <div className="text-sm text-muted-foreground">Loading preview...</div>
            </div>
          </div>}

        {iframeError ? <div className="flex flex-col items-center justify-center h-full p-8">
            <AlertCircle className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">Preview not available</h3>
            <p className="text-sm text-muted-foreground text-center mb-4">
              This website doesn't allow embedding in iframes
            </p>
            <Button variant="outline" size="sm" onClick={onOpenInNewTab} className="gap-2">
              <ExternalLink className="h-4 w-4" />
              Open in new tab
            </Button>
          </div> : null}

        {iframeUrl && <div key={`iframe-container-${url}`} className="w-full h-full">
            {/* biome-ignore lint/a11y/noNoninteractiveElementInteractions: iframe requires load/error handlers for status UI */}
            <iframe ref={iframeReference} src={iframeUrl} title={`Preview of ${iframeUrl}`} className={cn('w-full h-full border-0', isIframeLoading || iframeError ? 'invisible' : 'visible')} sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox" referrerPolicy="no-referrer" loading="lazy" onLoad={handleIframeLoad} onError={() => {
          if (!iframeReference.current) {
            return;
          }
          onIframeError();
        }} />
          </div>}
      </div>
    </div>;
}
export default LinkIframeViewer;